import React, { useMemo, useState } from "react";
import { Typography } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { SbTable, SbTabs, SbEmpty, CaretDownOutlined } from "uiCore";
import { BackLinkStyled } from "../../RoleDetailV2.styles";
import { DetailHeadingRowStyled, PlanTableWrapperStyled } from "../ViewPlanningV2.styles";
import { PAIC_ROLE_TAB, PAIC_ROLE_TABS } from "../ViewPlanningV2.constants";
import { groupPaicRolesByTab, tableEmptyLocale } from "../ViewPlanningV2.utils";
import { buildPaicRoleColumns } from "../useViewPlanningColumns";
import { displayProjectName } from "Pages/OrgDesign/orgDesignVersionColumns";
import PaicRoleEmployeesPanel from "./PaicRoleEmployeesPanel";

const { Title } = Typography;

const roleRowKey = (record) => record.role_key || record.jd_sb_unique_id;

// PAIC Redesign Plan ▸ the plan's organization (org redesign version) — its
// roles split into Role Reduced / Role Added. `roles` is null while loading.
// isOrganizationUnavailable (no visible version, so `organization` is null, or
// it was archived since the list loaded) replaces the tabs with a message and
// the heading shows just the plan name. A role row expands in place to the
// employees the plan shortlisted.
const PaicOrganizationRoles = ({
  planId,
  planName,
  organization,
  isOrganizationUnavailable = false,
  roles,
  activeTab,
  onTabChange,
  onBack,
}) => {
  // Same accordion behaviour as Talent Mobility Manager's role table:
  // openRoleKeys is what's open now; everOpenedRoleKeys only grows and feeds
  // antd's expandedRowKeys, so a panel stays mounted after its first open
  // (fetched once) and can animate closed (RolePanelCollapse).
  const [openRoleKeys, setOpenRoleKeys] = useState([]);
  const [everOpenedRoleKeys, setEverOpenedRoleKeys] = useState([]);
  const toggleRole = (record) => {
    const key = roleRowKey(record);
    setOpenRoleKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
    setEverOpenedRoleKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
  };

  const rolesByTab = useMemo(() => groupPaicRolesByTab(roles, PAIC_ROLE_TABS), [roles]);
  // Filter options depend only on the rows; the caret column on what's open,
  // so expanding a role doesn't rebuild every tab's filters.
  const baseColumnsByTab = useMemo(
    () => Object.fromEntries(PAIC_ROLE_TABS.map((tab) => [tab.key, buildPaicRoleColumns(rolesByTab[tab.key])])),
    [rolesByTab]
  );
  // Expand/collapse cue, same as Talent Mobility Manager's role table; the row handles the click.
  const expandColumn = useMemo(
    () => ({
      title: "View employees",
      key: "expand",
      align: "center",
      width: 120,
      onHeaderCell: () => ({ className: "tm-col-header-center" }),
      render: (_, record) => <CaretDownOutlined rotate={openRoleKeys.includes(roleRowKey(record)) ? 180 : 0} />,
    }),
    [openRoleKeys]
  );
  const columnsByTab = useMemo(
    () => Object.fromEntries(PAIC_ROLE_TABS.map((tab) => [tab.key, [...baseColumnsByTab[tab.key], expandColumn]])),
    [baseColumnsByTab, expandColumn]
  );

  // A tab restored from history that no longer exists falls back to the
  // default instead of a blank pane.
  const visibleActiveTab = PAIC_ROLE_TABS.some((tab) => tab.key === activeTab) ? activeTab : PAIC_ROLE_TAB.REDUCED;

  const tabItems = PAIC_ROLE_TABS.map((tab) => ({
    key: tab.key,
    label: roles ? `${tab.label} (${rolesByTab[tab.key].length})` : tab.label,
    // data-nested-scroll-root: the scroll container PaicRoleEmployeesPanel
    // sizes its nested list to.
    children: (
      <PlanTableWrapperStyled data-nested-scroll-root>
        <SbTable
          columns={columnsByTab[tab.key]}
          dataSource={rolesByTab[tab.key]}
          rowKey={roleRowKey}
          sticky
          onRow={(record) => ({ onClick: () => toggleRole(record) })}
          expandable={{
            expandedRowKeys: everOpenedRoleKeys,
            showExpandColumn: false,
            // The panel supplies its own padding (NestedTableWrapperStyled);
            // this zeroes antd's cell padding around it (sharedTable.styles.js).
            expandedRowClassName: () => "tm-role-panel-cell",
            expandedRowRender: (record) => (
              <PaicRoleEmployeesPanel
                planId={planId}
                orgRedesignId={organization?.sb_org_redesign_unique_id}
                role={record}
                isOpen={openRoleKeys.includes(roleRowKey(record))}
              />
            ),
          }}
          locale={tableEmptyLocale(roles === null ? null : rolesByTab[tab.key], tab.emptyMessage)}
        />
      </PlanTableWrapperStyled>
    ),
  }));

  // Fragment root so the tab pane's flex column (TabsBodyStyled) can hand
  // the remaining height to the inner tabs and their table wrapper.
  return (
    <>
      <BackLinkStyled onClick={onBack}>
        <ArrowLeftOutlined /> Back to Plans
      </BackLinkStyled>
      <DetailHeadingRowStyled>
        <Title level={5}>
          {organization
            ? `${planName} — ${displayProjectName(organization) || organization.sb_org_redesign_unique_id}`
            : planName}
        </Title>
      </DetailHeadingRowStyled>
      {isOrganizationUnavailable ? (
        <SbEmpty description="This plan's organization is no longer available." />
      ) : (
        <SbTabs activeKey={visibleActiveTab} onChange={onTabChange} items={tabItems} />
      )}
    </>
  );
};

export default PaicOrganizationRoles;
